import CheckIcon from '@mui/icons-material/Check';
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Menu, MenuItem, MenuList, ListItemIcon, ListItemText, Paper, Box, Button } from '@mui/material';
import { useContext, useState, useRef } from 'react';
import { MapContext } from '../context';
import { db } from '../instant';
import { StationType } from '../schema';

const ITEM_HEIGHT = 48;

export default function MapFilterMenu() {
    const ctx = useContext(MapContext);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [stationTypeMenuAnchor, setStationTypeMenuAnchor] = useState<null | HTMLElement>(null);
    const stationTypeMenuRef = useRef<HTMLLIElement>(null);

    const { data: stationTypesData } = db.useQuery({ stationTypes: {} });

    const open = Boolean(anchorEl);
    const stationTypeMenuOpen = Boolean(stationTypeMenuAnchor);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setStationTypeMenuAnchor(null);
    };

    const handleStationTypeMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setStationTypeMenuAnchor(event.currentTarget);
    };

    const handleStationTypeMenuClose = () => {
        setStationTypeMenuAnchor(null);
    };

    const toggleStationType = (stationTypeId: string) => {
        const newSelected = new Set(ctx.selectedStationTypeIds);
        if (newSelected.has(stationTypeId)) {
            newSelected.delete(stationTypeId);
        } else {
            newSelected.add(stationTypeId);
        }
        ctx.setSelectedStationTypeIds(newSelected);
    };

    const toggleTodoOnly = () => {
        ctx.setTodoOnlyFilter(!ctx.todoOnlyFilter);
        handleClose();
    };

    const toggleNotesOnly = () => {
        ctx.setNotesOnlyFilter(!ctx.notesOnlyFilter);
        handleClose();
    };

    const clearAllStationTypes = () => {
        ctx.setSelectedStationTypeIds(new Set());
        setStationTypeMenuAnchor(null);
        setAnchorEl(null);
    };

    return (
        <Paper
            elevation={2}
            sx={{
                position: 'absolute',
                left: '20px',
                top: '20px',
                zIndex: '1001',
                backgroundColor: 'white',
                borderRadius: '5px',
            }}
        >
            <IconButton
                aria-label="options"
                id="options-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
            >
                <FilterListIcon />
            </IconButton>
            <Menu
                id="filter-menu"
                MenuListProps={{
                    'aria-labelledby': 'options-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    style: {
                        maxHeight: ITEM_HEIGHT * 4.5,
                        width: '25ch',
                    },
                }}
            >
                <MenuList>
                    <MenuItem ref={stationTypeMenuRef} onClick={handleStationTypeMenuOpen}>
                        <ListItemIcon>{ctx.selectedStationTypeIds.size > 0 ? <CheckIcon /> : <></>}</ListItemIcon>
                        <ListItemText>By Station Type</ListItemText>
                        <ChevronRightIcon sx={{ ml: 'auto' }} />
                    </MenuItem>

                    <MenuItem onClick={toggleTodoOnly}>
                        <ListItemIcon>{ctx.todoOnlyFilter ? <CheckIcon /> : <></>}</ListItemIcon>
                        <ListItemText>To-do</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={toggleNotesOnly}>
                        <ListItemIcon>{ctx.notesOnlyFilter ? <CheckIcon /> : <></>}</ListItemIcon>
                        <ListItemText>Has Notes</ListItemText>
                    </MenuItem>
                </MenuList>
            </Menu>

            {/* Nested menu for station types */}
            <Menu
                id="station-type-submenu"
                anchorEl={stationTypeMenuAnchor}
                open={stationTypeMenuOpen}
                onClose={handleStationTypeMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        maxHeight: ITEM_HEIGHT * 8,
                        width: '30ch',
                    },
                }}
            >
                <MenuList>
                    <Box sx={{ p: 1, pb: 0.5 }}>
                        <Button size="small" fullWidth variant="outlined" onClick={clearAllStationTypes}>
                            Clear All
                        </Button>
                    </Box>
                    {stationTypesData?.stationTypes
                        ?.sort((a, b) => (a.order as number) - (b.order as number))
                        .map((stationType: StationType) => (
                            <MenuItem key={stationType.id} onClick={() => toggleStationType(stationType.id)}>
                                <ListItemIcon>
                                    {ctx.selectedStationTypeIds.has(stationType.id) ? <CheckIcon /> : <Box width={24} />}
                                </ListItemIcon>
                                {stationType.iconName && (
                                    <Box
                                        component="img"
                                        src={`/icons/${stationType.iconName}`}
                                        alt={stationType.name}
                                        sx={{ width: 24, height: 24, mr: 1 }}
                                    />
                                )}
                                <ListItemText>{stationType.name}</ListItemText>
                            </MenuItem>
                        ))}
                </MenuList>
            </Menu>
        </Paper>
    );
}
